import { createContext, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { AirportListItemType } from '@lp/features/RoutePlanner/types';
import { airportsMapper } from '@lp/features/RoutePlanner/components/RouteLegForm/utils';
import { BaseAviaLegSchemaValues } from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import { AirportFboType } from '@/api/apis/facilities';
import { queryClient } from '@/queries';
import { airportsQuery } from '@/queries/airports';
import { useWatchFields } from '@/hooks/useWatchFields';
import { getDefaultFbo } from '@/queries/route-planner/utils';
import { RouteLegTypeEnum } from '@/api/apis/route';

type AirportProviderProps = {
  locationType: 'departureLocation' | 'arrivalLocation';
  handleDateTime: (timezone: string) => void;
};

type AirportLocationContextType = {
  locationType: 'departureLocation' | 'arrivalLocation';
  currentFbos: AirportFboType[];
  setCurrentFbos: (fbos: AirportFboType[]) => void;
  handleSelectedAirport: (airport: AirportListItemType) => Promise<void>;
};

export const getAirportData = async (id: string): Promise<AirportListItemType | null> => {
  const data = await queryClient.ensureQueryData(airportsQuery.item(id, 'ids'));

  return data ? airportsMapper(data) : null;
};

export const initialAirportLocationContextValues: AirportLocationContextType = {
  locationType: 'departureLocation',
  currentFbos: [],
  setCurrentFbos: () => {},
  handleSelectedAirport: () => Promise.resolve(),
};

export const AirportLocationContext = createContext<AirportLocationContextType>(initialAirportLocationContextValues);

export const AirportLocationProvider = ({
  locationType,
  handleDateTime,
  children,
}: PropsWithChildren<AirportProviderProps>) => {
  const [currentFbos, setCurrentFbos] = useState<AirportFboType[]>([]);

  const { setValue } = useFormContext<BaseAviaLegSchemaValues>();

  const {
    [locationType]: locationValue,
    legType,
    formType,
  } = useWatchFields<BaseAviaLegSchemaValues>([locationType, 'legType', 'formType', 'segmentType'] as const);

  const { calculateLegTimeDuration, setLegDuration } = useLegFormContext();

  const handleSelectedAirport = useCallback(
    async (airport: AirportListItemType) => {
      setCurrentFbos(airport.fbos);

      const defaultFbo = getDefaultFbo(airport.fbos, locationValue.fboId);

      if (legType !== RouteLegTypeEnum.CommercialFlightAviaLeg) {
        setValue(`${locationType}.fboId`, defaultFbo ? defaultFbo.id : '', { shouldValidate: true });
        setValue(`${locationType}.fboAddressId`, defaultFbo ? defaultFbo.addresses[0].id : 0);
      }
      setValue(`${locationType}.maoId`, airport.maoId);
      setValue(`${locationType}.displayName`, airport.name);
      setValue(`${locationType}.timezone`, airport.location.timezone);
      setValue(`${locationType}.longitude`, airport.location.longitude);
      setValue(`${locationType}.latitude`, airport.location.latitude);

      const data = await calculateLegTimeDuration(legType);
      setLegDuration(formType, legType, data);
      handleDateTime(airport.location.timezone);
    },
    [
      formType,
      legType,
      locationValue.fboId,
      locationType,
      handleDateTime,
      setValue,
      calculateLegTimeDuration,
      setLegDuration,
    ],
  );

  const value = useMemo(
    () => ({
      locationType,
      currentFbos,
      setCurrentFbos,
      handleSelectedAirport,
    }),
    [locationType, currentFbos, handleSelectedAirport],
  );

  return <AirportLocationContext.Provider value={value}>{children}</AirportLocationContext.Provider>;
};
